// The application header: the wordmark and the account trigger, in normal document flow.
//
// Previously `AccountMenu` rendered on its own, `position: absolute` against `#root` — see
// that file's own history and `docs/archived/13-visual-refresh.md`, which calls out the overlap
// with page content that caused. A real header bar removes the need for that: the wordmark
// links back to the plans list (replacing the "Your plans" back-link every plan screen used
// to invent for itself) and the account trigger sits at the opposite end of the same row.
//
// Rendered once, above the router, the same way `AccountMenu` was — but the bar itself
// renders whether or not there is a session, so `/signup` and `/invite` carry the same
// brand as every signed-in screen. Only the account trigger gates on the token: there is no
// account to open until there is a session. Signed out, the wordmark is not a link — `/`
// redirects straight back to `/signup` with no session, so a link there would be a no-op —
// and it carries no "your plans" framing, because there are no plans yet to name.

import { Link } from 'react-router-dom';
import { useSyncExternalStore } from 'react';

import type { ApiClient } from '../api/client.js';
import { session } from '../session.js';
import { AccountMenu } from './AccountMenu.js';
import { Wordmark } from './Wordmark.js';

export type HeaderProps = {
  client: ApiClient;
};

export function Header({ client }: HeaderProps): JSX.Element {
  const token = useSyncExternalStore(
    session.subscribe,
    () => session.token(),
    () => null,
  );
  return (
    <header className="header">
      <div className="header__inner">
        {token === null ? (
          // `Wordmark` already gives its `<svg>` `role="img"` and `aria-label="Mor"`, so
          // there is nothing for a wrapping label to add here — an outer `aria-label` would
          // just repeat the same name a second time.
          <div className="header__brand">
            <Wordmark className="header__wordmark" />
          </div>
        ) : (
          <Link to="/" className="header__brand" aria-label="Mor, your plans">
            <Wordmark className="header__wordmark" />
          </Link>
        )}
        {token === null ? null : <AccountMenu client={client} />}
      </div>
    </header>
  );
}
